import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  HideWithCSS,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
} from "@biblioteksentralen/react";
import { ReactNode, useState } from "react";
import { ChevronDown, Sliders, X } from "react-feather";
import { ResolvedFacets, Filter, ResolvedFacet } from "./facetUtils";
import { getTypedEntries } from "@libry-content/types";
import { DefaultContainer } from "../ContentContainer";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { FilterTag } from "./FilterTag";

interface Props<A extends string> {
  facets: ResolvedFacets<A>;
  toggleFacet: (facet: A, value: string) => void;
  isValidating: boolean;
  customFacets?: { component: ReactNode; filterTags: ReactNode[]; label: string }[];
}

export const Facets = <A extends string>({ facets, toggleFacet, customFacets, isValidating }: Props<A>) => {
  const facetEntries = getTypedEntries(facets);
  const currentFacetFilters =
    facetEntries.flatMap(([facetName, facet]) => facet.currentFilters.map((filter) => ({ filter, facetName }))) ?? [];

  return (
    <>
      <HideWithCSS below="sm" marginBottom="0.5rem">
        <FacetButtons
          facets={facets}
          toggleFacet={toggleFacet}
          isValidating={!!isValidating}
          customFacets={customFacets}
        />
      </HideWithCSS>
      <HideWithCSS above="sm">
        <FacetAccordion
          facets={facets}
          toggleFacet={toggleFacet}
          isValidating={!!isValidating}
          customFacets={customFacets}
        />
      </HideWithCSS>
      <Stack
        direction="row"
        paddingBottom={[...currentFacetFilters, customFacets].length ? 4 : 0}
        gridGap={2}
        wrap="wrap"
        justifyContent="flex-start"
      >
        {customFacets?.flatMap(({ filterTags }) => filterTags.map((filterTag) => filterTag))}
        {currentFacetFilters.map(({ facetName, filter }) => (
          <FilterTag key={filter.value} filter={filter} onClick={() => toggleFacet(facetName, filter.value)} />
        ))}
      </Stack>
    </>
  );
};

const FacetButtons = <A extends string>({ facets, toggleFacet, customFacets: extraFacets, isValidating }: Props<A>) => {
  const facetEntries = getTypedEntries(facets);

  return (
    <HStack>
      {facetEntries.map(
        ([facetName, facet]) =>
          facet.availableFilters.length > 1 && (
            <FilterPopover key={facetName} label={facet.label} placement="bottom-start">
              <FilterOptions<A>
                facetName={facetName}
                filters={facet.availableFilters}
                resolvedFacet={facet}
                toggleFacet={toggleFacet}
              />
            </FilterPopover>
          )
      )}
      {extraFacets?.map(({ component, label }) => (
        <FilterPopover key={label} label={label} placement="bottom-start">
          {component}
        </FilterPopover>
      ))}
      {isValidating && <Spinner />}
    </HStack>
  );
};

const FacetAccordion = <A extends string>({
  facets,
  toggleFacet,
  customFacets: extraFacets,
  isValidating,
}: Props<A>) => {
  const { t } = useTranslation();
  const facetEntries = getTypedEntries(facets);
  const allFacets = [...facetEntries, ...(extraFacets ?? [])];
  const oneOrLessFacets = allFacets.length <= 1 ? true : false;
  const [filterMobileOpen, setFilterMobileOpen] = useState(oneOrLessFacets ? true : false);

  return (
    <>
      {!oneOrLessFacets && (
        <DefaultContainer paddingInlineStart="0">
          <Button
            isLoading={isValidating}
            variant="outline"
            leftIcon={filterMobileOpen ? <X /> : <Sliders style={{ transform: "rotate(90deg)" }} size="1em" />}
            marginBottom="0.5rem"
            onClick={() => setFilterMobileOpen(!filterMobileOpen)}
          >
            {t("Filter")}
          </Button>
        </DefaultContainer>
      )}
      {filterMobileOpen && (
        <Accordion paddingTop="1rem" paddingBottom="1rem" allowMultiple allowToggle>
          {facetEntries.map(
            ([facetName, facet]) =>
              facet.availableFilters.length > 1 && (
                <FilterAccordion key={facetName} label={facet.label}>
                  <FilterOptions<A>
                    facetName={facetName}
                    filters={facet.availableFilters}
                    resolvedFacet={facet}
                    toggleFacet={toggleFacet}
                  />
                </FilterAccordion>
              )
          )}
          {extraFacets?.map(({ component, label }) => (
            <FilterAccordion key={label} label={label}>
              <Box display="flex" justifyContent="center" padding="0" paddingBottom="1rem">
                {component}
              </Box>
            </FilterAccordion>
          ))}
        </Accordion>
      )}
    </>
  );
};

const FilterPopover = ({ label, children, ...rest }: { label: string; children: ReactNode } & PopoverProps) => {
  return (
    <Popover {...rest}>
      <PopoverTrigger>
        <Button size="md" variant="outline" rightIcon={<ChevronDown />}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent width="auto">
        <PopoverBody>{children}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const FilterAccordion = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <AccordionItem>
      <h2>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            <Text fontWeight={600}>{label}</Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel paddingBottom="1rem">{children}</AccordionPanel>
    </AccordionItem>
  );
};

const FilterOptions = <A extends string>(props: {
  facetName: A;
  filters: Filter[];
  resolvedFacet: ResolvedFacet;
  toggleFacet: (facet: A, value: string) => void;
}) => {
  return (
    <Stack direction="column">
      {props.filters.map(({ label, value }) => {
        const isChecked = !!props.resolvedFacet.currentFilters.find((filter) => filter.value === value);
        return (
          <Checkbox key={value} onChange={() => props.toggleFacet(props.facetName, value)} isChecked={isChecked}>
            <Text fontSize=".9rem">{label}</Text>
          </Checkbox>
        );
      })}
    </Stack>
  );
};
